import React, { useState, useEffect, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend, BarChart, Bar, AreaChart, Area,
} from 'recharts';
import { fmt, api } from '../utils/api';
import TickerWatchlist from './TickerWatchlist';
import InvestmentWithdrawals from './InvestmentWithdrawals';
import WithdrawalCalculator from './WithdrawalCalculator';

const ALLOCATION_COLORS = ['#00d4aa', '#5eead4', '#fbbf24'];

// Finds the snapshot closest to ~30 days before the most recent one, and
// returns the % change in total net worth since then
function getMoMChange(history) {
  if (!history || history.length < 2) return null;
  const latest = history[history.length - 1];
  const latestDate = new Date(latest.snapshot_date);
  const targetDate = new Date(latestDate);
  targetDate.setDate(targetDate.getDate() - 30);

  let closest = null;
  let closestDiff = Infinity;
  for (const h of history) {
    const diff = Math.abs(new Date(h.snapshot_date) - targetDate);
    if (diff < closestDiff) {
      closestDiff = diff;
      closest = h;
    }
  }
  if (!closest || closest === latest) return null;

  const before = parseFloat(closest.total_net_worth);
  const after = parseFloat(latest.total_net_worth);
  if (before === 0) return null;

  return {
    pct: ((after - before) / Math.abs(before)) * 100,
    dollar: after - before,
  };
}

export default function Investments({ refreshKey, onRefresh }) {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ address: '', estimatedValue: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [rateInput, setRateInput] = useState(4.0);
  const [savingRate, setSavingRate] = useState(false);

  const load = useCallback(async () => {
    try {
      const [d, h] = await Promise.all([
        api.getInvestments(),
        api.getInvestmentHistory(),
      ]);
      setData(d);
      setHistory(h);
      setRateInput(d.withdrawalRate);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Refetch on any app-wide change too - a new sync or a change made
  // elsewhere (like Budget Settings, which also edits the withdrawal rate)
  // can affect what's shown here
  useEffect(() => { load(); }, [load, refreshKey]);

  const submitProperty = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.address.trim() || !form.estimatedValue) {
      setError('Address and estimated value are required.');
      return;
    }
    setSubmitting(true);
    try {
      await api.addRealEstate({
        address: form.address.trim(),
        estimatedValue: parseFloat(form.estimatedValue),
        notes: form.notes.trim() || undefined,
      });
      setForm({ address: '', estimatedValue: '', notes: '' });
      setShowForm(false);
      load();
      onRefresh?.();
    } catch (err) {
      setError('Failed to add property.');
    } finally {
      setSubmitting(false);
    }
  };

  const removeProperty = async (id) => {
    if (!window.confirm('Remove this property?')) return;
    await api.deleteRealEstate(id);
    load();
    onRefresh?.();
  };

  const commitRate = async (rate) => {
    setSavingRate(true);
    try {
      await api.updateWithdrawalRate(rate);
      load();
      onRefresh?.();
    } catch (err) {
      console.error(err);
    } finally {
      setSavingRate(false);
    }
  };

  if (loading) return <div style={styles.panel}><p style={styles.empty}>Loading...</p></div>;
  if (!data) return null;

  return (
    <div style={styles.grid}>
      {/* Net worth summary */}
      <div style={styles.panel}>
        <h2 style={styles.title}>💎 Net Worth</h2>
        <div style={styles.netWorthRow}>
          <div style={styles.bigStat}>
            <span style={styles.bigStatLabel}>Total Net Worth</span>
            <span style={styles.bigStatValue}>{fmt.currency(data.totalNetWorth)}</span>
          </div>
          <div style={styles.bigStat}>
            <span style={styles.bigStatLabel}>Liquid Net Worth</span>
            <span style={{ ...styles.bigStatValue, color: '#00d4aa' }}>
              {fmt.currency(data.liquidNetWorth)}
            </span>
          </div>
        </div>

        {(data.totalNetWorthChangeToday !== null || data.liquidNetWorthChangeToday !== null) && (
          <div style={styles.todayGrowthRow}>
            {data.totalNetWorthChangeToday !== null && (
              <TodayGrowthBadge label={`Total, ${data.dailyChangeLabel || 'today'}`} value={data.totalNetWorthChangeToday} />
            )}
            {data.liquidNetWorthChangeToday !== null && (
              <TodayGrowthBadge label={`Liquid, ${data.dailyChangeLabel || 'today'}`} value={data.liquidNetWorthChangeToday} />
            )}
          </div>
        )}

        {Math.abs(data.investmentTransfersOutToday || 0) > 0.5 && (
          <InvestmentGrowthBreakdown
            label={data.dailyChangeLabel || 'today'}
            balanceChange={data.investmentBalanceChangeToday}
            transfersOut={data.investmentTransfersOutToday}
            marketChange={data.investmentGrowthToday}
          />
        )}
        {Math.abs(data.investmentTransfersOutThisMonth || 0) > 0.5 && (
          <InvestmentGrowthBreakdown
            label="this month"
            balanceChange={data.investmentBalanceChangeThisMonth}
            transfersOut={data.investmentTransfersOutThisMonth}
            marketChange={data.investmentGrowthThisMonth}
          />
        )}

        <div className="breakdown-grid-4" style={styles.breakdownGrid}>
          <BreakdownItem label="Cash" value={data.cashTotal} color="#00d4aa" />
          <BreakdownItem label="Investments" value={data.investmentTotal} color="#5eead4" />
          <BreakdownItem label="Real Estate" value={data.realEstateTotal} color="#fbbf24" />
          <BreakdownItem label="Credit Owed" value={-data.creditOwed} color="#ef4444" />
        </div>
      </div>

      {/* Net worth trend */}
      <div style={styles.panel}>
        <div style={styles.header}>
          <h2 style={styles.title}>📈 Net Worth Trend</h2>
          {(() => {
            const mom = getMoMChange(history);
            if (!mom) return null;
            const up = mom.dollar >= 0;
            return (
              <span style={{
                ...styles.momBadge,
                color: up ? '#00d4aa' : '#ef4444',
                borderColor: up ? '#00d4aa' : '#ef4444',
              }}>
                {up ? '▲' : '▼'} {Math.abs(mom.pct).toFixed(1)}% (~30d)
              </span>
            );
          })()}
        </div>
        {!history || history.length < 2 ? (
          <p style={styles.empty}>
            Building history — this chart tracks a snapshot each time you
            visit this page, so it'll fill in over the coming weeks and
            months. It can't reconstruct history from before today.
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={history.map((h) => ({
              date: new Date(h.snapshot_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
              total: parseFloat(h.total_net_worth),
              liquid: parseFloat(h.liquid_net_worth),
            }))} margin={{ top: 10, right: 24, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e2a3a" />
              <XAxis dataKey="date" tick={{ fill: '#6b7280', fontSize: 10, fontFamily: "'DM Mono', monospace" }} axisLine={false} tickLine={false} />
              <YAxis
                domain={['dataMin - dataMin * 0.02', 'dataMax + dataMax * 0.02']}
                tick={{ fill: '#6b7280', fontSize: 10, fontFamily: "'DM Mono', monospace" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => v >= 1000000 ? `$${(v / 1000000).toFixed(2)}M` : `$${(v / 1000).toFixed(0)}k`}
              />
              <Tooltip
                contentStyle={{ background: '#1e2a3a', border: '1px solid #374151', borderRadius: '8px' }}
                labelStyle={{ color: '#9ca3af' }}
                formatter={(v) => fmt.currency(v)}
              />
              <Line type="monotone" dataKey="total" stroke="#fbbf24" strokeWidth={2} dot={false} name="Total Net Worth" />
              <Line type="monotone" dataKey="liquid" stroke="#00d4aa" strokeWidth={2} dot={false} name="Liquid Net Worth" />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Asset allocation */}
      {(data.cashTotal + data.investmentTotal + data.realEstateTotal) > 0 && (
        <div style={styles.panel}>
          <h2 style={styles.title}>🥧 Asset Allocation</h2>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={[
                  { name: 'Cash', value: data.cashTotal },
                  { name: 'Investments', value: data.investmentTotal },
                  { name: 'Real Estate', value: data.realEstateTotal },
                ].filter((d) => d.value > 0)}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={85}
                paddingAngle={3}
              >
                {[0, 1, 2].map((i) => (
                  <Cell key={i} fill={ALLOCATION_COLORS[i]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ background: '#1e2a3a', border: '1px solid #374151', borderRadius: '8px' }}
                formatter={(v) => fmt.currency(v)}
              />
              <Legend
                formatter={(value) => <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>{value}</span>}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Real estate vs. liquid net worth over time */}
      <div style={styles.panel}>
        <h2 style={styles.title}>🏘 Real Estate vs. Liquid Net Worth</h2>
        {!history || history.length < 2 ? (
          <p style={styles.empty}>
            Same as the trend chart above — this fills in over time as
            snapshots accumulate.
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={history.map((h) => ({
              date: new Date(h.snapshot_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
              liquid: parseFloat(h.liquid_net_worth),
              realEstate: parseFloat(h.real_estate_total),
            }))} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e2a3a" />
              <XAxis dataKey="date" tick={{ fill: '#6b7280', fontSize: 10, fontFamily: "'DM Mono', monospace" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#6b7280', fontSize: 10, fontFamily: "'DM Mono', monospace" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                contentStyle={{ background: '#1e2a3a', border: '1px solid #374151', borderRadius: '8px' }}
                formatter={(v) => fmt.currency(v)}
              />
              <Legend
                formatter={(value) => <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>{value}</span>}
              />
              <Area type="monotone" dataKey="liquid" stackId="1" stroke="#00d4aa" fill="#00d4aa" fillOpacity={0.3} name="Liquid" />
              <Area type="monotone" dataKey="realEstate" stackId="1" stroke="#fbbf24" fill="#fbbf24" fillOpacity={0.3} name="Real Estate" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Withdrawal rate + budget suggestion */}
      <div style={styles.panel}>
        <h2 style={styles.title}>📐 Budget from Investments</h2>
        <p style={styles.helpText}>
          Based on your liquid net worth ({fmt.currency(data.liquidNetWorth)}) and a safe
          withdrawal rate, here's how much you could reasonably draw from
          investments each year.
        </p>

        <div style={styles.sliderRow}>
          <span style={styles.sliderLabel}>Withdrawal rate</span>
          <span style={styles.sliderValue}>{rateInput.toFixed(1)}%</span>
        </div>
        <input
          type="range"
          min="0"
          max="8"
          step="0.5"
          value={rateInput}
          onChange={(e) => setRateInput(parseFloat(e.target.value))}
          onMouseUp={(e) => commitRate(parseFloat(e.target.value))}
          onTouchEnd={(e) => commitRate(parseFloat(e.target.value))}
          style={styles.slider}
        />
        <div style={styles.sliderTicks}>
          <span>Conservative (3%)</span>
          <span>Standard (4%)</span>
          <span>Aggressive (6%+)</span>
        </div>

        <div style={styles.suggestionBox}>
          <Row label="Max safe annual draw" value={fmt.currency(data.maxSafeWithdrawal)} />
          <Row label="Your annual income (YTD, this year)" value={fmt.currency(data.annualIncome)} />
          <Row label="Current annual budget" value={fmt.currency(data.annualBudget)} />
          <div style={styles.divider} />
          <Row
            label="Suggested draw from investments"
            value={fmt.currency(data.suggestedDraw)}
            highlight
          />
        </div>
        {savingRate && <p style={styles.savingNote}>Saving...</p>}
      </div>

      {/* Investment accounts */}
      <div style={styles.panel}>
        <h2 style={styles.title}>📈 Investment Accounts</h2>
        {data.investmentAccounts.length === 0 ? (
          <p style={styles.empty}>
            No investment accounts connected yet. Click "Connect Account" on
            the Accounts tab and link Schwab, Fidelity, or another brokerage.
          </p>
        ) : (
          <>
            {data.investmentAccounts.length > 1 && (
              <ResponsiveContainer width="100%" height={Math.max(120, data.investmentAccounts.length * 45)}>
                <BarChart
                  layout="vertical"
                  data={data.investmentAccounts.map((a) => ({
                    name: a.name.length > 20 ? a.name.slice(0, 20) + '…' : a.name,
                    value: parseFloat(a.current_balance),
                  }))}
                  margin={{ top: 0, right: 20, left: 0, bottom: 0 }}
                >
                  <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 10, fontFamily: "'DM Mono', monospace" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="name" tick={{ fill: '#9ca3af', fontSize: 11, fontFamily: "'Syne', sans-serif" }} axisLine={false} tickLine={false} width={140} />
                  <Tooltip
                    contentStyle={{ background: '#1e2a3a', border: '1px solid #374151', borderRadius: '8px' }}
                    formatter={(v) => fmt.currency(v)}
                  />
                  <Bar dataKey="value" fill="#5eead4" radius={[0, 4, 4, 0]} maxBarSize={28} />
                </BarChart>
              </ResponsiveContainer>
            )}
            <div style={{ marginTop: data.investmentAccounts.length > 1 ? '1rem' : 0 }}>
              {data.investmentAccounts.map((acct) => (
                <div key={acct.id} style={styles.acctRow}>
                  <div>
                    <div style={styles.acctName}>{acct.name}</div>
                    <div style={styles.acctInstitution}>{acct.institution_name}</div>
                  </div>
                  <div style={styles.acctBalance}>{fmt.currency(acct.current_balance)}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Real estate */}
      <div style={styles.panel}>
        <div style={styles.header}>
          <h2 style={styles.title}>🏠 Real Estate</h2>
          <button style={styles.addBtn} onClick={() => setShowForm((s) => !s)}>
            {showForm ? 'Cancel' : '+ Add Property'}
          </button>
        </div>

        {showForm && (
          <form style={styles.form} onSubmit={submitProperty}>
            <input
              style={styles.input}
              placeholder="Address"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
            />
            <input
              style={styles.input}
              type="number"
              step="0.01"
              placeholder="Estimated value"
              value={form.estimatedValue}
              onChange={(e) => setForm({ ...form, estimatedValue: e.target.value })}
            />
            <input
              style={styles.input}
              placeholder="Notes (optional)"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
            {error && <p style={styles.error}>{error}</p>}
            <button style={styles.submitBtn} type="submit" disabled={submitting}>
              {submitting ? 'Adding...' : 'Add Property'}
            </button>
          </form>
        )}

        {data.realEstate.length === 0 && !showForm ? (
          <p style={styles.empty}>
            No properties added. Add your home or rental estimates here to
            include them in your total net worth.
          </p>
        ) : (
          data.realEstate.map((prop) => (
            <div key={prop.id} style={styles.acctRow}>
              <div>
                <div style={styles.acctName}>{prop.address}</div>
                {prop.notes && <div style={styles.acctInstitution}>{prop.notes}</div>}
              </div>
              <div style={styles.rowRight}>
                <div style={styles.acctBalance}>{fmt.currency(prop.estimated_value)}</div>
                <button style={styles.deleteBtn} onClick={() => removeProperty(prop.id)}>✕</button>
              </div>
            </div>
          ))
        )}
      </div>

      <InvestmentWithdrawals targetWithdrawalRate={data.withdrawalRate} liquidNetWorth={data.liquidNetWorth} />
      <WithdrawalCalculator startingBalance={data.liquidNetWorth} />

      <TickerWatchlist />
    </div>
  );
}

function InvestmentGrowthBreakdown({ label, balanceChange, transfersOut, marketChange }) {
  const isWithdrawal = transfersOut > 0;
  return (
    <div style={styles.transferBreakdown}>
      <div style={styles.transferBreakdownHeader}>
        <span style={styles.transferBreakdownIcon}>🔄</span>
        <span style={styles.transferBreakdownTitle}>
          Investment balance change ({label}) includes a transfer
        </span>
      </div>
      <div style={styles.transferBreakdownRow}>
        <span style={styles.transferBreakdownLabel}>Raw balance change</span>
        <span style={{ ...styles.transferBreakdownValue, color: balanceChange >= 0 ? '#00d4aa' : '#ef4444' }}>
          {balanceChange >= 0 ? '+' : ''}{fmt.currency(balanceChange)}
        </span>
      </div>
      <div style={styles.transferBreakdownRow}>
        <span style={styles.transferBreakdownLabel}>
          {isWithdrawal ? 'Transferred out (not a loss)' : 'Contributed in (not growth)'}
        </span>
        <span style={{ ...styles.transferBreakdownValue, color: '#fbbf24' }}>
          {isWithdrawal ? '+' : '−'}{fmt.currency(Math.abs(transfersOut))}
        </span>
      </div>
      <div style={{ ...styles.transferBreakdownRow, ...styles.transferBreakdownFinal }}>
        <span style={styles.transferBreakdownLabel}>Actual market change</span>
        <span style={{ ...styles.transferBreakdownValue, color: marketChange >= 0 ? '#00d4aa' : '#ef4444', fontWeight: '800' }}>
          {marketChange >= 0 ? '+' : ''}{fmt.currency(marketChange)}
        </span>
      </div>
    </div>
  );
}

function TodayGrowthBadge({ label, value }) {
  const up = value >= 0;
  const color = up ? '#00d4aa' : '#ef4444';
  return (
    <div style={{ ...styles.todayBadge, borderColor: color, background: `${color}15` }}>
      <span style={styles.todayBadgeLabel}>{label}</span>
      <span style={{ ...styles.todayBadgeValue, color }}>
        {up ? '▲' : '▼'} {fmt.currency(Math.abs(value))}
      </span>
    </div>
  );
}

function BreakdownItem({ label, value, color }) {
  return (
    <div style={styles.breakdownItem}>
      <span style={styles.breakdownLabel}>{label}</span>
      <span style={{ ...styles.breakdownValue, color }}>{fmt.currency(value)}</span>
    </div>
  );
}

function Row({ label, value, highlight }) {
  return (
    <div style={styles.suggestionRow}>
      <span style={{ color: highlight ? '#fff' : '#9ca3af', fontWeight: highlight ? '700' : '400' }}>
        {label}
      </span>
      <span style={{
        color: highlight ? '#00d4aa' : '#e5e7eb',
        fontWeight: highlight ? '700' : '500',
        fontFamily: "'DM Mono', monospace",
      }}>
        {value}
      </span>
    </div>
  );
}

const styles = {
  grid: { display: 'flex', flexDirection: 'column', gap: '1rem' },
  panel: {
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '16px',
    padding: '1.5rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  title: {
    margin: '0 0 1rem',
    fontSize: '1rem',
    fontWeight: '700',
    color: '#fff',
    fontFamily: "'Syne', sans-serif",
  },
  momBadge: {
    border: '1px solid',
    borderRadius: '999px',
    padding: '3px 10px',
    fontSize: '0.75rem',
    fontWeight: '700',
    fontFamily: "'DM Mono', monospace",
    marginBottom: '1rem',
  },
  todayGrowthRow: {
    display: 'flex',
    gap: '0.6rem',
    marginBottom: '1.25rem',
    flexWrap: 'wrap',
  },
  transferBreakdown: {
    background: 'rgba(251, 191, 36, 0.05)',
    border: '1px solid rgba(251, 191, 36, 0.25)',
    borderRadius: '12px',
    padding: '0.9rem 1.1rem',
    marginBottom: '1.25rem',
  },
  transferBreakdownHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '0.6rem',
  },
  transferBreakdownIcon: { fontSize: '0.85rem' },
  transferBreakdownTitle: {
    color: '#fbbf24',
    fontSize: '0.75rem',
    fontWeight: '700',
    fontFamily: "'Syne', sans-serif",
  },
  transferBreakdownRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.78rem',
    padding: '3px 0',
    fontFamily: "'DM Mono', monospace",
  },
  transferBreakdownLabel: {
    color: '#9ca3af',
    fontFamily: "'Syne', sans-serif",
  },
  transferBreakdownValue: {
    fontWeight: '700',
  },
  transferBreakdownFinal: {
    borderTop: '1px solid rgba(251, 191, 36, 0.2)',
    marginTop: '4px',
    paddingTop: '8px',
  },
  todayBadge: {
    border: '1px solid',
    borderRadius: '10px',
    padding: '0.5rem 0.85rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  todayBadgeLabel: {
    color: '#9ca3af',
    fontSize: '0.65rem',
    fontFamily: "'Syne', sans-serif",
  },
  todayBadgeValue: {
    fontSize: '0.9rem',
    fontWeight: '700',
    fontFamily: "'DM Mono', monospace",
  },
  netWorthRow: {
    display: 'flex',
    gap: '2rem',
    marginBottom: '1.25rem',
    flexWrap: 'wrap',
  },
  bigStat: { display: 'flex', flexDirection: 'column', gap: '2px' },
  bigStatLabel: {
    color: '#6b7280',
    fontSize: '0.75rem',
    fontFamily: "'Syne', sans-serif",
  },
  bigStatValue: {
    fontSize: '1.6rem',
    fontWeight: '700',
    color: '#fff',
    fontFamily: "'DM Mono', monospace",
  },
  breakdownGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '0.75rem',
  },
  breakdownItem: {
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '10px',
    padding: '0.75rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  breakdownLabel: {
    color: '#6b7280',
    fontSize: '0.7rem',
    fontFamily: "'Syne', sans-serif",
  },
  breakdownValue: {
    fontSize: '1rem',
    fontWeight: '700',
    fontFamily: "'DM Mono', monospace",
  },
  helpText: {
    color: '#6b7280',
    fontSize: '0.8rem',
    lineHeight: '1.5',
    marginBottom: '1rem',
  },
  sliderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '0.35rem',
  },
  sliderLabel: {
    color: '#9ca3af',
    fontSize: '0.8rem',
    fontFamily: "'Syne', sans-serif",
  },
  sliderValue: {
    color: '#00d4aa',
    fontSize: '0.9rem',
    fontWeight: '700',
    fontFamily: "'DM Mono', monospace",
  },
  slider: { width: '100%', accentColor: '#00d4aa' },
  sliderTicks: {
    display: 'flex',
    justifyContent: 'space-between',
    color: '#4b5563',
    fontSize: '0.65rem',
    marginBottom: '1.25rem',
    fontFamily: "'Syne', sans-serif",
  },
  suggestionBox: {
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '10px',
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  suggestionRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.85rem',
    fontFamily: "'Syne', sans-serif",
  },
  divider: { borderTop: '1px solid #1e2a3a', margin: '0.25rem 0' },
  savingNote: { color: '#4b5563', fontSize: '0.7rem', marginTop: '0.5rem' },
  acctRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.6rem 0.75rem',
    background: '#0a0f1e',
    borderRadius: '8px',
    marginBottom: '0.4rem',
    border: '1px solid #1e2a3a',
  },
  acctName: {
    color: '#e5e7eb',
    fontSize: '0.875rem',
    fontWeight: '500',
    fontFamily: "'Syne', sans-serif",
  },
  acctInstitution: {
    color: '#4b5563',
    fontSize: '0.7rem',
    marginTop: '2px',
  },
  acctBalance: {
    fontSize: '0.9rem',
    fontWeight: '600',
    fontFamily: "'DM Mono', monospace",
    color: '#fff',
  },
  rowRight: { display: 'flex', alignItems: 'center', gap: '0.6rem' },
  deleteBtn: {
    background: 'transparent',
    border: 'none',
    color: '#4b5563',
    cursor: 'pointer',
    fontSize: '0.85rem',
  },
  addBtn: {
    background: 'transparent',
    color: '#00d4aa',
    border: '1px solid #00d4aa',
    borderRadius: '6px',
    padding: '6px 12px',
    fontSize: '0.75rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    marginBottom: '1rem',
    padding: '1rem',
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '10px',
  },
  input: {
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '6px',
    padding: '0.5rem 0.6rem',
    color: '#fff',
    fontSize: '0.85rem',
    fontFamily: "'Syne', sans-serif",
  },
  error: { color: '#ef4444', fontSize: '0.75rem', margin: 0 },
  submitBtn: {
    background: '#00d4aa',
    color: '#0a0f1e',
    border: 'none',
    borderRadius: '6px',
    padding: '0.6rem',
    fontSize: '0.8rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  empty: {
    color: '#4b5563',
    fontSize: '0.875rem',
    textAlign: 'center',
    padding: '1rem 0',
    margin: 0,
  },
};
